import { useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import {
    X,
    Mail,
    Lock,
    User,
    Eye,
    EyeOff,
} from "lucide-react";

import api from '../../api/axios';
import { setToken, setUser } from '../../api/auth';
import { useToast } from "../../context/ToastContext";
import { redirect, useNavigate } from "react-router-dom";

export default function AuthPopup({
    isOpen,
    onClose,
    authMode = '',
}) {
    const [mode, setMode] = useState("login");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const { success, error } = useToast();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        org_name: "",
    });

    const [formError, setError] = useState("");

    useEffect(() => {
        if (authMode) setMode(authMode);
    }, [authMode]);

    if (!isOpen) return null;

    const updateField = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");

        if (!form.email.trim())
            return setError("Email is required.");

        if (!form.password.trim())
            return setError("Password is required.");

        if (mode === "signup") {
            if (!form.name.trim())
                return setError("Name is required.");

            if (!form.org_name.trim())
                return setError("Organization Name is required.");

            if (form.password.length < 6)
                return setError("Password must be at least 6 characters.");

            if (form.password !== form.confirmPassword)
                return setError("Passwords do not match.");
        }

        try {
            setLoading(true);

            if (mode === "login") {
                await handleLogin?.({
                    email: form.email,
                    password: form.password,
                });
            } else {
                await handleSignup?.({
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    org_name: form.org_name,
                });
            }
        } catch (err) {
            setError(err?.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async ({ email, password }) => {
        // Call your login API
        try {
            setLoading(true);
            const response = await api.post("/auth/login", { email, password });
            if (response && response.data && response.data.success) {
                setUser(response?.data?.user);
                setToken(response?.data?.token);
                setLoading(false);
                success("Login successful...");
                onClose();
                navigateTo(response);
            } else {
                setLoading(false);
                success(response.data.message);
            }
        } catch (err) {
            setLoading(false);
            console.log(err?.data?.message);
            error(err?.data?.message);
        }
    };

    const handleSignup = async ({ name, email, password, org_name }) => {
        // Call your signup API
        const payload = {
            "user_name": name,
            "email": email,
            "password": password,
            "role": "organization",
            "org_name": org_name
        };

        try {
            setLoading(true);
            const authResponse = await api.post("/auth/register", payload);
            if (authResponse && authResponse.data && authResponse.data.success) {
                setUser(authResponse?.data?.user);
                setToken(authResponse?.data?.token);
                setLoading(false);
                onClose();
                success("Signup successful...");
                navigateTo(authResponse);
            } else {
                setLoading(false);
                success(authResponse.data.message);
            }
        } catch (err) {
            setLoading(false);
            console.log(err?.data?.message);
            error(err?.data?.message);
        }

    };

    const handleGoogleLogin = async (response) => {
        try {
            setLoading(true);
            const authResponse = await api.post("/auth/google", response);
            if (authResponse && authResponse.data && authResponse.data.success) {
                setUser(authResponse?.data?.user);
                setToken(authResponse?.data?.token);
                setLoading(false);
                onClose();
                success("Login successful...");
                navigateTo(authResponse);
            } else {
                setLoading(false);
                success(authResponse.data.message);
            }
        } catch (err) {
            setLoading(false);
            console.log(err?.data?.message);
            error(err?.data?.message);
        }
    };

    const navigateTo = (response) => {
        let redirectTo = "/";
        if (response?.data?.user?.role === 'admin') {
            redirectTo = "/admin";
        } else if (response?.data?.user?.role === 'organization') {
            redirectTo = "/organization";
        } else {
            redirectTo = "/user";
        }
        navigate(redirectTo);
    };


    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
            <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">

                {/* Header */}

                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-2 text-white">

                    <button
                        onClick={onClose}
                        className="absolute right-5 top-5 hover:opacity-80"
                    >
                        <X size={22} />
                    </button>

                    <h2 className="text-3xl font-bold">
                        {mode === "login"
                            ? "Welcome Back"
                            : "Create Account"}
                    </h2>

                    <p className="text-white/80">
                        Login to continue
                    </p>
                </div>

                <div className="p-8">

                    {/* Tabs */}

                    <div className="mb-6 flex rounded-xl bg-gray-100 p-1">

                        <button
                            onClick={() => setMode("login")}
                            className={`flex-1 rounded-lg py-2 transition ${mode === "login"
                                ? "bg-white shadow font-semibold"
                                : "text-gray-500"
                                }`}
                        >
                            Login
                        </button>

                        <button
                            onClick={() => setMode("signup")}
                            className={`flex-1 rounded-lg py-2 transition ${mode === "signup"
                                ? "bg-white shadow font-semibold"
                                : "text-gray-500"
                                }`}
                        >
                            Sign Up
                        </button>

                    </div>

                    {/* Google */}

                    <div className="w-full flex justify-center">
                        <GoogleLogin
                            width="100%"
                            shape="pill"
                            theme="outline"
                            text={mode == 'signup' ? 'signup_with' : 'signin_with'}
                            onSuccess={handleGoogleLogin}
                            onError={() => setError("Google login failed.")}
                        />
                    </div>

                    <div className="my-6 flex items-center">

                        <div className="h-px flex-1 bg-gray-200" />

                        <span className="mx-4 text-sm text-gray-400">
                            OR
                        </span>

                        <div className="h-px flex-1 bg-gray-200" />

                    </div>

                    {formError && (
                        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">
                            {formError}
                        </div>
                    )}

                    <form
                        onSubmit={handleSubmit}
                        className="space-y-4"
                    >

                        {mode === "signup" && (
                            <div className="relative">
                                <User
                                    size={18}
                                    className="absolute left-4 top-4 text-gray-400"
                                />

                                <input
                                    className="w-full rounded-xl border pl-11 pr-4 py-3 outline-none focus:border-indigo-500"
                                    placeholder="User Name"
                                    value={form.name}
                                    onChange={(e) =>
                                        updateField("name", e.target.value)
                                    }
                                />
                            </div>
                        )}

                        <div className="relative">

                            <Mail
                                size={18}
                                className="absolute left-4 top-4 text-gray-400"
                            />

                            <input
                                type="email"
                                placeholder="Email"
                                className="w-full rounded-xl border pl-11 pr-4 py-3 outline-none focus:border-indigo-500"
                                value={form.email}
                                onChange={(e) =>
                                    updateField("email", e.target.value)
                                }
                            />

                        </div>

                        {mode === "signup" && (
                            <div className="relative">
                                <User
                                    size={18}
                                    className="absolute left-4 top-4 text-gray-400"
                                />

                                <input
                                    className="w-full rounded-xl border pl-11 pr-4 py-3 outline-none focus:border-indigo-500"
                                    placeholder="Organization Name"
                                    value={form.org_name}
                                    onChange={(e) =>
                                        updateField("org_name", e.target.value)
                                    }
                                />
                            </div>
                        )}


                        <div className="relative">

                            <Lock
                                size={18}
                                className="absolute left-4 top-4 text-gray-400"
                            />

                            <input
                                type={
                                    showPassword ? "text" : "password"
                                }
                                placeholder="Password"
                                className="w-full rounded-xl border pl-11 pr-11 py-3 outline-none focus:border-indigo-500"
                                value={form.password}
                                onChange={(e) =>
                                    updateField("password", e.target.value)
                                }
                            />

                            <button
                                type="button"
                                className="absolute right-4 top-4 text-gray-400"
                                onClick={() =>
                                    setShowPassword(!showPassword)
                                }
                            >
                                {showPassword ? (
                                    <EyeOff size={18} />
                                ) : (
                                    <Eye size={18} />
                                )}
                            </button>

                        </div>

                        {mode === "signup" && (
                            <div className="relative">

                                <Lock
                                    size={18}
                                    className="absolute left-4 top-4 text-gray-400"
                                />

                                <input
                                    type="password"
                                    placeholder="Confirm Password"
                                    className="w-full rounded-xl border pl-11 pr-4 py-3 outline-none focus:border-indigo-500"
                                    value={form.confirmPassword}
                                    onChange={(e) =>
                                        updateField(
                                            "confirmPassword",
                                            e.target.value
                                        )
                                    }
                                />

                            </div>
                        )}

                        {mode === "login" && (
                            <div className="flex justify-end">
                                <button
                                    onClick={() => navigate('/forgot-password')}
                                    type="button"
                                    className="text-sm text-indigo-600 hover:underline"
                                >
                                    Forgot Password?
                                </button>
                            </div>
                        )}

                        <button
                            disabled={loading}
                            className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {loading
                                ? "Please wait..."
                                : mode === "login"
                                    ? "Login"
                                    : "Create Account"}
                        </button>

                    </form>

                    <p className="mt-6 text-center text-sm text-gray-500">

                        {mode === "login"
                            ? "Don't have an account?"
                            : "Already have an account?"}

                        <button
                            className="ml-2 font-semibold text-indigo-600"
                            onClick={() =>
                                setMode(
                                    mode === "login"
                                        ? "signup"
                                        : "login"
                                )
                            }
                        >
                            {mode === "login"
                                ? "Sign Up"
                                : "Login"}
                        </button>

                    </p>

                </div>
            </div>
        </div>
    );
}